import { Button } from "@/components/ui/button";
import type {
	ApprovalDecisionPayload,
	ApprovalProposal,
	StructuredApprovalRecordedContent,
} from "@/services/types/agent-gateway";
import { Loader2 } from "lucide-react";
import type { ReactNode } from "react";
import { useState } from "react";

function approvalChannelLabel(channel: string): string {
	return channel === "linkedin_connection"
		? "LinkedIn connection request"
		: channel;
}

type ApprovalActionBoxProps = {
	children: ReactNode;
	heading: string;
	headingId: string;
};

type ApprovalCardProps = {
	agentName: string;
	proposal: ApprovalProposal;
	canApprove: boolean;
	actionsDisabled?: boolean;
	isPending: boolean;
	errorMessage?: string | null;
	onDecision: (payload: ApprovalDecisionPayload) => void;
};

type RecordedApprovalCardProps = {
	agentName: string;
	record: StructuredApprovalRecordedContent;
	isRetryPending?: boolean;
	onRetryContinuation?: (approvalId: string) => void;
};

function ApprovalActionBox({
	children,
	heading,
	headingId,
}: ApprovalActionBoxProps) {
	return (
		<section
			aria-labelledby={headingId}
			className="w-full min-w-0 overflow-hidden rounded-[12px] border border-[#FDB022] bg-[rgba(253,176,34,0.08)] p-[14px] md:px-5 md:py-4"
		>
			<h3
				className="font-medium text-[#212529] text-sm leading-normal [overflow-wrap:anywhere]"
				id={headingId}
			>
				{heading}
			</h3>

			<div className="mt-[10px] min-w-0 md:mt-3">{children}</div>
		</section>
	);
}

function formatDecision(
	decision: StructuredApprovalRecordedContent["decision"],
) {
	if (decision === "approved") {
		return "Approved";
	}
	if (decision === "rejected") {
		return "Rejected";
	}
	return "Changes requested";
}

export function RecordedApprovalCard({
	agentName,
	record,
	isRetryPending = false,
	onRetryContinuation,
}: RecordedApprovalCardProps) {
	const headingId = `approval-recorded-${record.approval_id}`;
	const decidedAt = new Intl.DateTimeFormat("en-US", {
		dateStyle: "medium",
		timeStyle: "short",
	}).format(new Date(record.decided_at));

	return (
		<ApprovalActionBox
			heading={`Approval Recorded · ${agentName}`}
			headingId={headingId}
		>
			<dl className="grid min-w-0 gap-3 text-[#343a40] text-sm leading-[1.4] md:grid-cols-2 md:text-base">
				<div>
					<dt className="text-[#6c757d] text-xs md:text-sm">Decision</dt>
					<dd className="mt-1 font-medium text-[#212529]">
						{formatDecision(record.decision)}
					</dd>
				</div>
				<div>
					<dt className="text-[#6c757d] text-xs md:text-sm">Decided by</dt>
					<dd className="mt-1 font-medium text-[#212529] [overflow-wrap:anywhere]">
						{record.decided_by.name ?? "Supplier administrator"}
					</dd>
				</div>
				<div className="md:col-span-2">
					<dt className="text-[#6c757d] text-xs md:text-sm">Recorded</dt>
					<dd className="mt-1 font-medium text-[#212529]">{decidedAt}</dd>
				</div>
				{record.revision_notes ? (
					<div className="md:col-span-2">
						<dt className="text-[#6c757d] text-xs md:text-sm">
							Requested changes
						</dt>
						<dd className="mt-1 whitespace-pre-wrap text-[#343a40] [overflow-wrap:anywhere]">
							{record.revision_notes}
						</dd>
					</div>
				) : null}
			</dl>
			{record.continuation_status === "failed" ? (
				<div className="mt-3 border-[#FDB022]/40 border-t pt-3">
					<p className="text-[#7A2E0E] text-sm">
						The decision was recorded, but the agent did not continue.
					</p>
					<Button
						className="mt-2"
						disabled={isRetryPending || !onRetryContinuation}
						onClick={() => onRetryContinuation?.(record.approval_id)}
						size="sm"
						type="button"
						variant="outline"
					>
						{isRetryPending ? (
							<Loader2 aria-hidden="true" className="animate-spin" />
						) : null}
						{isRetryPending ? "Retrying…" : "Retry continuation"}
					</Button>
				</div>
			) : null}
		</ApprovalActionBox>
	);
}

export function ApprovalCard({
	agentName,
	proposal,
	canApprove,
	actionsDisabled = false,
	isPending,
	errorMessage,
	onDecision,
}: ApprovalCardProps) {
	const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([]);
	const [showRevision, setShowRevision] = useState(false);
	const [revisionNotes, setRevisionNotes] = useState("");
	const headingId = `approval-${proposal.approval_id}`;
	const decisionActionsDisabled = actionsDisabled || isPending;

	const toggleTarget = (targetId: string) => {
		setSelectedTargetIds((current) =>
			current.includes(targetId)
				? current.filter((id) => id !== targetId)
				: [...current, targetId],
		);
	};

	const submitDecision = (decision: ApprovalDecisionPayload["decision"]) => {
		onDecision({
			approval_id: proposal.approval_id,
			decision,
			selected_target_ids:
				proposal.approval_type === "verified_leads" && decision === "approved"
					? selectedTargetIds
					: [],
			revision_notes:
				decision === "revision_requested" ? revisionNotes.trim() : null,
		});
	};

	return (
		<ApprovalActionBox
			heading={`Approval Required · ${agentName}`}
			headingId={headingId}
		>
			{proposal.approval_type === "verified_leads" ? (
				<div className="min-w-0">
					<p className="text-[#343a40] text-sm leading-[1.4] md:text-base">
						{proposal.title}
					</p>
					<fieldset className="mt-[10px] space-y-2 md:mt-3">
						<legend className="sr-only">Verified contact targets</legend>
						{proposal.targets.map((target) => (
							<label
								className="flex min-w-0 items-start gap-3 rounded-lg border border-[#FDB022]/40 bg-white p-3"
								key={target.target_id}
							>
								<input
									aria-label={`${target.lead_label} ${target.channel}: ${target.value}`}
									checked={selectedTargetIds.includes(target.target_id)}
									className="mt-0.5 size-4 shrink-0 accent-brand-darkGreen"
									disabled={!canApprove || decisionActionsDisabled}
									onChange={() => toggleTarget(target.target_id)}
									type="checkbox"
								/>
								<span className="min-w-0 text-sm leading-[1.4]">
									<strong className="block font-medium text-[#212529] [overflow-wrap:anywhere]">
										{target.lead_label}
									</strong>
									<span className="mt-0.5 block text-[#495057] capitalize [overflow-wrap:anywhere]">
										{target.channel}: {target.value}
									</span>
									{target.person_name || target.role_title ? (
										<span className="mt-1 block text-[#495057] [overflow-wrap:anywhere]">
											{[target.person_name, target.role_title]
												.filter(Boolean)
												.join(" · ")}
										</span>
									) : null}
									{target.qualification_score != null ? (
										<span className="mt-1 block font-medium text-brand-darkGreen">
											Qualification score: {target.qualification_score}/100
										</span>
									) : null}
									{target.persona_classification ? (
										<span className="mt-1 block text-[#6c757d] capitalize">
											{target.persona_classification.split("_").join(" ")}
										</span>
									) : null}
								</span>
							</label>
						))}
					</fieldset>
				</div>
			) : (
				<dl className="grid min-w-0 gap-3 text-[#343a40] text-sm leading-[1.4] md:grid-cols-2 md:text-base">
					<div>
						<dt className="text-[#6c757d] text-xs md:text-sm">Channel</dt>
						<dd className="mt-1 font-medium text-[#212529] capitalize">
							{approvalChannelLabel(proposal.channel)}
						</dd>
					</div>
					<div>
						<dt className="text-[#6c757d] text-xs md:text-sm">Recipient</dt>
						<dd className="mt-1 font-medium text-[#212529] [overflow-wrap:anywhere]">
							{proposal.recipient}
						</dd>
					</div>
					<div className="md:col-span-2">
						<dt className="text-[#6c757d] text-xs md:text-sm">Subject</dt>
						<dd className="mt-1 font-medium text-[#212529] [overflow-wrap:anywhere]">
							{proposal.subject || "No subject"}
						</dd>
					</div>
					<div className="md:col-span-2">
						<dt className="text-[#6c757d] text-xs md:text-sm">Content</dt>
						<dd className="mt-1 whitespace-pre-wrap text-[#343a40] [overflow-wrap:anywhere]">
							{proposal.content_preview}
						</dd>
					</div>
				</dl>
			)}

			{!canApprove ? (
				<p className="mt-[10px] text-[#6c757d] text-xs md:mt-3 md:text-sm">
					This approval is read only.
				</p>
			) : null}

			{showRevision && canApprove ? (
				<div className="mt-[10px] md:mt-3">
					<label
						className="font-medium text-[#212529] text-sm"
						htmlFor={`${headingId}-revision`}
					>
						Requested changes
					</label>
					<textarea
						className="mt-2 min-h-24 w-full rounded-lg border border-[#CED5DA] bg-white p-3 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
						disabled={decisionActionsDisabled}
						id={`${headingId}-revision`}
						maxLength={2_000}
						onChange={(event) => setRevisionNotes(event.target.value)}
						value={revisionNotes}
					/>
				</div>
			) : null}

			{errorMessage ? (
				<p className="mt-[10px] text-destructive text-sm md:mt-3" role="alert">
					{errorMessage}
				</p>
			) : null}

			{canApprove ? (
				<div className="mt-[10px] flex flex-wrap items-center gap-[10px] bg-white md:mt-3 md:gap-3">
					<Button
						className="h-auto rounded-lg bg-[#102D03] px-4 py-[9px] text-sm text-white shadow-none hover:bg-[#102D03]/90 md:px-[18px] md:py-2.5 md:text-base"
						disabled={
							decisionActionsDisabled ||
							(proposal.approval_type === "verified_leads" &&
								selectedTargetIds.length === 0)
						}
						onClick={() => submitDecision("approved")}
						type="button"
					>
						{isPending ? (
							<>
								<Loader2 aria-hidden="true" className="animate-spin" />
								Recording…
							</>
						) : (
							"Approve"
						)}
					</Button>
					<Button
						className="h-auto rounded-lg border-[#CED5DA] bg-white px-4 py-[9px] text-[#212529] text-sm shadow-none hover:bg-[#f8f9fa] md:px-[18px] md:py-2.5 md:text-base"
						disabled={decisionActionsDisabled}
						onClick={() => submitDecision("rejected")}
						type="button"
						variant="outline"
					>
						Reject
					</Button>
					{showRevision ? (
						<Button
							className="h-auto rounded-lg px-2 py-[9px] text-[#495057] text-sm shadow-none md:py-2.5 md:text-base"
							disabled={
								decisionActionsDisabled || revisionNotes.trim().length === 0
							}
							onClick={() => submitDecision("revision_requested")}
							type="button"
							variant="ghost"
						>
							Submit changes
						</Button>
					) : (
						<Button
							className="h-auto rounded-lg px-2 py-[9px] text-[#495057] text-sm shadow-none md:py-2.5 md:text-base"
							disabled={decisionActionsDisabled}
							onClick={() => setShowRevision(true)}
							type="button"
							variant="ghost"
						>
							Request changes
						</Button>
					)}
				</div>
			) : null}
		</ApprovalActionBox>
	);
}
